import { delay, eventChannel, buffers, END } from 'redux-saga'
import {
	all,
	actionChannel,
	select,
	fork,
	cancel,
	take,
	call,
	put,
	cancelled,
	race,
	takeLatest,
	throttle,
} from 'redux-saga/effects'
import jwtDecode from 'jwt-decode'
import callAny from './helpers/call_any'
import config from 'config'
import formatError from './helpers/error_formatter'
import actionTypes from 'redux/actions/actionTypes'
import interactiveVideoActions from 'redux/actions/interactiveVideoActions'
import routeActions from 'redux/actions/routeActions'
import interactiveVideoSelectors from 'redux/selectors/interactive_videos'
import interactiveVideoSessionInitializationSelector from 'redux/selectors/interactive_videos/session_initialization'
import interactiveVideoParticipantStateSelector from 'redux/selectors/interactive_videos/participant_state'
import interactiveVideoSettingsSaveSelector from 'redux/selectors/interactive_videos/interactive_video_settings_save_selector'
import activitySelector from 'redux/selectors/interactive_videos/interactive_video_participant_activity_selector'
import { interactiveVideoSync, userSync, questionSync } from 'sync'
import socketConnect from 'sync/helpers/socketConnection'
import viewTypes from 'helpers/enums/viewTypes'
import currentViewSelector from 'redux/selectors/ui/currentViewSelector'
import interactiveVideoPmSelector from 'redux/selectors/interactive_videos/interactive_video_pm_selector'
import userRoleSelector from 'redux/selectors/interactive_videos/interactive_video_user_selector'
import userRoles from '../../helpers/enums/userRoles'
import actionUserTypes from 'helpers/enums/actionUserTypes'
import confirmTypes from 'helpers/enums/confirmTypes'
import buttonUserTypes from 'helpers/enums/buttonUserTypes'
import activityTypes from 'helpers/enums/activityTypes'
import draftStateTypes from 'helpers/enums/interactiveVideo/draftStateTypes'
import isStudentPacedSelector from 'redux/selectors/interactive_videos/is_student_paced_selector'
import sessionTypes from 'helpers/enums/sessionTypes'
import videoSourceTypes from 'helpers/enums/interactiveVideo/videoSourceTypes'
import { parse } from 'qs'
import youtubeCaptionsFormatter from 'helpers/interactive_video/youtube_captions_formatter'
import payloadTypes from 'helpers/enums/interactiveVideo/payloadTypes'

function *initializeStatus() {
	while (true) {
		yield take([actionTypes.INTERACTIVE_VIDEO_SESSION_INITIALIZE])
		const interactiveVideoRequestData =
			yield select(interactiveVideoSessionInitializationSelector)
		const isSessionDisabled = interactiveVideoRequestData.isGuest

		if (!isSessionDisabled) {
			const connection = yield fork(
				connectToSession,
				interactiveVideoRequestData
			)
			yield take([
				actionTypes.INTERACTIVE_VIDEO_SESSION_FINALIZE,
			])
			yield cancel(connection)
		}
	}
}

function *getPreviewData() {
	try {
		const interactiveVideoSessionData = yield select(interactiveVideoSessionInitializationSelector)
		const response =
			yield callAny(
				interactiveVideoSync.getPreviewData,
				interactiveVideoSessionData.jwt,
				interactiveVideoSessionData.mediaUid,
			)
		yield put(interactiveVideoActions.interactiveVideoPreviewFetchSuccess(response))
	} catch (error) {
		yield put(interactiveVideoActions.interactiveVideoPreviewFetchFailed(
			formatError(error)
		))
	}
}

function *initializeStandalonePreview() {
	try {
		const interactiveVideoStandalonePreviewData = yield select(interactiveVideoSessionInitializationSelector)

		const slideResponse = yield callAny(interactiveVideoSync.getSlideData,
			interactiveVideoStandalonePreviewData.jwt,
			interactiveVideoStandalonePreviewData.externalUid,
		)

		const response =
			yield callAny(
				interactiveVideoSync.getPreviewData,
				interactiveVideoStandalonePreviewData.jwt,
				slideResponse.payload.entityUid,
			)
		yield put(interactiveVideoActions.interactiveVideoPreviewFetchSuccess(response))
	} catch (error) {
		yield put(interactiveVideoActions.interactiveVideoPreviewFetchFailed(
			formatError(error)
		))
	}
}

function *initializeStandaloneSession(uid, jwt, role) {
	const sessionData = yield callAny(interactiveVideoSync.createSession, uid, jwt, role)
	yield put(routeActions.initializeSessionInteractiveVideo(sessionData.uid, jwt))
}

function *handleInteractiveVideoEvents(socket) {
	let _lastMaximumTimeSaved = 0

	function *updateSettings() {
		const settings = yield select(interactiveVideoSettingsSaveSelector)
		socket.emit('updateSettings', settings)
	}

	const launchActivity = action => {
		socket.emit('launchActivity', {
			breakpointActivityUid: action.breakpointActivityUid,
		})
	}

	function *handleActivityReached(action) {
		const activity = yield select(activitySelector)
		const currentActivity = activity.currentActivity
		if (![activityTypes.NONE_QUESTION, activityTypes.MEDIA].includes(currentActivity.type)) {
			launchActivity(action)
		}
	}

	const launchActivityByParticipant = action => {
		socket.emit('launchActivityByParticipant', {
			breakpointActivityUid: action.breakpointActivityUid,
		})
	}


	function *updateMaximumTimeReached() {
		const {
			shouldPreventSkippingVideos,
			maximumTime,
		} = yield select(interactiveVideoSelectors.interactiveVideoStudentPacedSelector)
		if (shouldPreventSkippingVideos && maximumTime > _lastMaximumTimeSaved) {
			_lastMaximumTimeSaved = maximumTime
			socket.emit('updateMaximumTimeReached', {
				maximumTimeReached: maximumTime,
			})
		}
	}

	const clearActivity = () => {
		socket.emit('launchActivity', {
			breakpointActivityUid: '',
		})
	}

	function *clearActivityByParticipant() {
		const isStudentPaced = yield select(isStudentPacedSelector)
		if (isStudentPaced) {
			socket.emit('launchActivityByParticipant', {
				breakpointActivityUid: '',
			})
		}
	}

	function *startTimeChannel() {
		const eventActions = [
			actionTypes.INTERACTIVE_VIDEO_MEDIA_RANGE_EXITED,
		]
		const activityChan = yield actionChannel(eventActions, buffers.sliding(1))
		while (true) {
			try {
				const action = yield take(activityChan)
				yield call(socket.emit, 'updateTime', {
					time: action.time,
				})
			} catch (error) {
				// console.log(error)
			}
		}
	}

	function *startEventChannel() {

		let activityChan

		try {

			const eventActions = [
				actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_SELECTED,
				actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_CONFIRMED,
				actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_REACHED,
				actionTypes.INTERACTIVE_VIDEO_PLAY_SELECTED,
				actionTypes.INTERACTIVE_VIDEO_SEEK_SELECTED,
				actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE,
				actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_ACCEPTED,
				actionTypes.SESSION_SETTINGS_HIDE_STUDENT_NAMES_UPDATE,
				actionTypes.SESSION_SETTINGS_STREAM_MODE_UPDATE,
			]

			const interactiveVideoRequestData =
					yield select(interactiveVideoSessionInitializationSelector)

			const isStudent = interactiveVideoRequestData.role === userRoles.STUDENT

			const actionHandlers = {
				[actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_SELECTED]:
					isStudent ? launchActivityByParticipant : launchActivity,
				[actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_CONFIRMED]:
					isStudent ? launchActivityByParticipant : launchActivity,
				[actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_REACHED]:
					isStudent ? launchActivityByParticipant : handleActivityReached,
				[actionTypes.INTERACTIVE_VIDEO_PLAY_SELECTED]:
					isStudent ? clearActivityByParticipant : clearActivity,
				[actionTypes.INTERACTIVE_VIDEO_SEEK_SELECTED]:
					isStudent ? clearActivityByParticipant : clearActivity,
				[actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE]: updateSettings,
				[actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_ACCEPTED]:
					updateSettings,
				[actionTypes.SESSION_SETTINGS_HIDE_STUDENT_NAMES_UPDATE]: updateSettings,
				[actionTypes.SESSION_SETTINGS_STREAM_MODE_UPDATE]: updateSettings,
			}
			activityChan = yield actionChannel(eventActions)
			while (true) {
				try {
					const action = yield take(activityChan)
					yield call(actionHandlers[action.type], action)
				} catch (error) {
					// console.log(error)
				}
			}
		} catch (err) {
			// console.log(error)
		} finally {
			if (activityChan) {
				activityChan.close()
			}
		}
	}

	function syncMaximumTimeReached(action) {
		_lastMaximumTimeSaved = action.participantState.maximumTimeReached
	}

	function *startTimeReachedEventChannel() {
		// This channel it's only for maximum time reached purposes
		// The only one outside this one is startTimeReachedListener
		// because it uses another effect creator
		let activityChan

		try {
			const eventActions = [
				actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_REACHED,
				actionTypes.INTERACTIVE_VIDEO_END_OF_VIDEO_REACHED,
				actionTypes.INTERACTIVE_VIDEO_PARTICIPANT_STATE_FETCHED,
			]

			const actionHandlers = {
				[actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_REACHED]: updateMaximumTimeReached,
				[actionTypes.INTERACTIVE_VIDEO_END_OF_VIDEO_REACHED]: updateMaximumTimeReached,
				[actionTypes.INTERACTIVE_VIDEO_PARTICIPANT_STATE_FETCHED]: syncMaximumTimeReached,
			}

			activityChan = yield actionChannel(eventActions)
			while (true) {
				try {
					const action = yield take(activityChan)
					yield call(actionHandlers[action.type], action)
				} catch (error) {
					// console.log(error)
				}
			}
		} catch (err) {
			// console.log(error)
		} finally {
			if (activityChan) {
				activityChan.close()
			}
		}
	}

	function *startTimeReachedListener() {
		try {
			const timeInterval = 30000
			yield throttle(
				timeInterval,
				actionTypes.INTERACTIVE_VIDEO_MAX_TIME_REACHED,
				updateMaximumTimeReached
			)
		} catch (error) {
			// console.log(error)
		}
	}

	yield all([
		call(startEventChannel),
		call(startTimeReachedEventChannel),
		call(startTimeReachedListener),
		call(startTimeChannel),
	])

}

function *handleInitializeSettings(socket) {
	yield take([
		actionTypes.INTERACTIVE_VIDEO_SESSION_STATUS_FETCHED,
	])
	const role = yield select(userRoleSelector)
	if (role === userRoles.TEACHER) {
		const settings = yield select(interactiveVideoPmSelector)
		socket.emit('updateSettings', settings)
	}
	yield take([
		actionTypes.INTERACTIVE_VIDEO_SESSION_FINALIZE,
	])
}

function *getActivity(activityData, jwt) {
	const activityResponse = yield callAny(
		questionSync.get,
		activityData.activityUid,
		jwt,
	)
	return {
		type: activityData.activityType,
		[payloadTypes[activityData.activityType]]: {
			...activityResponse.payload,
			submissions: [],
			sharingOptions: {
				data: [],
			},
			answers: activityResponse.payload.answers.map(answer => {
				// TODO: fix here and in proto-repo media-session
				answer.media.type = 0
				return answer
			}),
		},
	}
}

function *getActivities(breakpoints, jwt) {
	const activities = breakpoints.map(breakpoint =>
		call(
			getActivity,
			breakpoint.activities[0],
			jwt,
		)
	)
	return yield all(activities)
}

function *handleInitializeParticipantSubmissions() {
	yield take([
		actionTypes.INTERACTIVE_VIDEO_SESSION_STATUS_FETCHED,
	])
	const isStudentPaced = yield select(isStudentPacedSelector)
	if (isStudentPaced) {
		try {
			const interactiveVideoParticipantOeqActivitiesData =
				yield select(
					interactiveVideoSelectors.interactiveVideoParticipantOeqActivityUidsSelector
				)
			const findSubmissionCalls = {}
			if (interactiveVideoParticipantOeqActivitiesData.activityUids.length > 0) {
				const findOeqSubmissionsCall = call(
					interactiveVideoSync.findOeqSubmissions,
					interactiveVideoParticipantOeqActivitiesData.jwt,
					interactiveVideoParticipantOeqActivitiesData.mediaSessionUid,
					interactiveVideoParticipantOeqActivitiesData.participantUid,
					interactiveVideoParticipantOeqActivitiesData.activityUids,
				)
				findSubmissionCalls[activityTypes.OPEN_ENDED_QUESTION] = findOeqSubmissionsCall
			}
			const interactiveVideoParticipantMcqActivitiesData =
			yield select(
				interactiveVideoSelectors.interactiveVideoParticipantMcqActivityUidsSelector
			)
			if (interactiveVideoParticipantMcqActivitiesData.activityUids.length > 0) {
				const findMcqSubmissionsCall = call(
					interactiveVideoSync.findMcqSubmissions,
					interactiveVideoParticipantMcqActivitiesData.jwt,
					interactiveVideoParticipantMcqActivitiesData.mediaSessionUid,
					interactiveVideoParticipantMcqActivitiesData.participantUid,
					interactiveVideoParticipantMcqActivitiesData.activityUids,
				)
				findSubmissionCalls[activityTypes.MULTIPLE_CHOICE_QUESTION] = findMcqSubmissionsCall
			}
			const submissions = yield all(findSubmissionCalls)
			const textAnswerSubmissions = submissions[activityTypes.OPEN_ENDED_QUESTION]
				? submissions[activityTypes.OPEN_ENDED_QUESTION].textAnswerSubmissions
				: []
			const selectableAnswerSubmissions = submissions[activityTypes.MULTIPLE_CHOICE_QUESTION]
				? submissions[activityTypes.MULTIPLE_CHOICE_QUESTION].selectableAnswerSubmissions
				: []
			yield put(
				interactiveVideoActions.participantSubmissionsFetched(
					textAnswerSubmissions,
					selectableAnswerSubmissions
				)
			)
		} catch (error) {
			yield put(interactiveVideoActions.participantSubmissionsFetchedFailed(
				formatError(error),
			))
		}
	}
	yield take([
		actionTypes.INTERACTIVE_VIDEO_SESSION_FINALIZE,
	])
}

function *handleInitializeParticipantState() {
	let lastSessionType = ''
	const participantData = yield select(interactiveVideoParticipantStateSelector)
	const isStudent = participantData.role === userRoles.STUDENT
	if (isStudent) {
		try {
			while (true) {
				yield take(actionTypes.INTERACTIVE_VIDEO_SESSION_STATUS_FETCHED)
				const participantStateData = yield select(interactiveVideoParticipantStateSelector)
				const currentSessionType = participantStateData.sessionType
				if (currentSessionType !== lastSessionType) {
					lastSessionType = currentSessionType
					if (currentSessionType === sessionTypes.HOMEWORK) {
						const participantState = yield callAny(
							interactiveVideoSync.getParticipantSessionState,
							participantStateData.uid,
							participantStateData.participantUid,
							participantStateData.jwt,
						)
						yield put(interactiveVideoActions.participantStateFetched(participantState))
						break
					}
				}
			}
		} catch (err) {
			// console.log(err)
		}
	}
	yield take([
		actionTypes.INTERACTIVE_VIDEO_SESSION_FINALIZE,
	])
}

function *connectToSession(videoSessionData) {

	const slideResponse = yield callAny(interactiveVideoSync.getSlideData,
		videoSessionData.jwt,
		videoSessionData.externalUid,
	)
	const media =
		yield callAny(
			interactiveVideoSync.getInteractiveVideoData,
			videoSessionData.jwt,
			slideResponse.payload.entityUid,
			'published',
		)
	const activities = yield call(
		getActivities,
		media.breakpoints,
		videoSessionData.jwt
	)

	let socket
	try {
		while (true) {
			socket = yield call(startSocketConnection, videoSessionData)
			yield race([
				call(subscribeToSocket, socket, media, activities),
				call(handleInteractiveVideoEvents, socket),
				call(handleInitializeSettings, socket),
				call(handleInitializeParticipantSubmissions),
				call(handleInitializeParticipantState),
			])
			yield call(socket.disconnect)
			yield call(delay, 2000)
		}
	} finally {
		if (yield cancelled()) {
			if (socket) {
				yield call(socket.disconnect)
			}
		}
	}
}

function subscribe(socket) {
	return eventChannel(emit => {
		socket.on('status', message => {
			emit({type: 'status', message})
		})
		socket.on('disconnect', () => {
			emit({type: 'disconnect'})
			emit(END)
		})
		socket.emit('getState')
		return () => {}
	})
}

function *subscribeToSocket(socket, mediaData, activities) {
	let shouldBuffer = false

	function buildCurrentActivity(currentActivity, breakpoints, activities) {
		if (currentActivity && currentActivity.type === activityTypes.ACTIVITY) {
			const breakpoint = breakpoints.find(breakpoint =>
				breakpoint.activities.find(activity => activity.activityUid === currentActivity.activityPayload.uid)
			)
			const breakpointActivityData = breakpoint.activities[0]
			const activity = activities.find(
				activity =>
					activity[payloadTypes[activity.type]].uid === breakpointActivityData.activityUid
			)
			return {
				...activity,
				[payloadTypes[activity.type]]: {
					...activity[payloadTypes[activity.type]],
					submissions: [],
					sharingOptions: {
						data: [],
					},
				},
			}
		} else {
			return currentActivity
		}
	}

	function *submitStatus(message) {
		if (shouldBuffer) {
			yield call(delay, 2000)
		}
		yield put(
			interactiveVideoActions.interactiveVideoSessionStatusFetched(
				message,
			)
		)
	}

	function *activateBuffer() {
		shouldBuffer = true
		yield call(delay, 2000)
		shouldBuffer = false
	}

	function *listenBufferingActions() {
		let bufferer
		const interactiveVideoRequestData =
				yield select(interactiveVideoSessionInitializationSelector)
		const isStudent = interactiveVideoRequestData.role === userRoles.STUDENT
		while (isStudent) {
			yield take([
				actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_CONFIRMED,
				actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER_CONFIRMED,
				actionTypes.INTERACTIVE_VIDEO_PLAY_SELECTED,
				actionTypes.INTERACTIVE_VIDEO_SEEK_SELECTED,
			])
			if (bufferer) {
				yield cancel(bufferer)
			}
			bufferer = yield fork(activateBuffer)
		}
	}

	function *listenSocketChannelMessages() {
		const channel = yield call(subscribe, socket)
		let isConnected = true
		let submission
		while (isConnected) {
			const channel_message = yield take(channel)
			if (channel_message.type === 'disconnect') {
				isConnected = false
			} else if (channel_message.type === 'status') {
				if (submission) {
					yield cancel(submission)
				}
				submission = yield fork(submitStatus, {
					...channel_message.message,
					currentActivity: buildCurrentActivity(
						channel_message.message.currentActivity,
						mediaData.breakpoints,
						activities,
					),
					mediaData,
					activities,
				})
			}
		}
	}


	const bufferFork = yield fork(listenBufferingActions)
	yield call(listenSocketChannelMessages)
	yield cancel(bufferFork)

}

function *startSocketConnection(videoSessionData) {
	let socket
	try {
		while (true) {
			socket = socketConnect(config.video_media_socket, ['websocket', 'polling'])
			const decodedJwt = jwtDecode(videoSessionData.jwt)
			yield call(socket.connect, {
				userId: decodedJwt.uid,
				externalUid: videoSessionData.externalUid,
				mediaSessionUid: videoSessionData.uid,
				sessionUid: decodedJwt.extra.session_uid,
				role: videoSessionData.role,
			})
			const participantUid = yield call(
				completeConnection,
				socket,
				videoSessionData
			)
			if (participantUid === false) {
				yield put(
					interactiveVideoActions.participantLimitReached()
				)
				break
			} else if (!!participantUid) {
				yield put(
					interactiveVideoActions.participantJoinedSession(
						participantUid,
					)
				)
				return socket
			} else {
				yield call(delay, 1000)
			}
		}
	} catch (err) {
		socket.disconnect()
	} finally {
		if (yield cancelled()) {
			socket.disconnect()
		}
	}
}

function completeConnection(socket, videoSessionData) {
	return new Promise(function(resolve) {

		const removeListeners = () => {
			socket.off('disconnected', disconnected)
		}

		const disconnected = () => {
			removeListeners()
			resolve('')
		}

		socket.on('disconnect', disconnected)
		socket.emit('join', {
			mediaSessionUid: videoSessionData.uid,
			nickname: videoSessionData.nickname,
			jwt: videoSessionData.jwt,
			version: '2.0.0',
		}).then(mediaParticipantUid => {
			resolve(mediaParticipantUid)
		})
	})
}

function *getAsset() {
	const currentView = yield select(currentViewSelector)
	if (
		currentView === viewTypes.INTERACTIVE_VIDEO_SESSION
		|| currentView === viewTypes.INTERACTIVE_VIDEO_PREVIEW
		|| currentView === viewTypes.LIBRARY
	) {
		const interactiveVideoRequestData =
			yield select(
				interactiveVideoSessionInitializationSelector
			)
		yield all([
			call(
				getAssetVideo,
				interactiveVideoRequestData.videoUid,
				interactiveVideoRequestData.jwt
			),
			call(
				getAssetImage,
				interactiveVideoRequestData.posterUid,
				interactiveVideoRequestData.jwt
			),
		])
	}
}

function *getAssetImage(posterUid, jwt) {
	try {
		const poster = yield callAny(
			interactiveVideoSync.getAssetImage,
			posterUid,
			jwt
		)
		yield put(
			interactiveVideoActions.interactiveVideoAssetPosterFetched(poster)
		)
	} catch (error) {
		const formattedError = formatError(error)
		yield put(
			interactiveVideoActions.interactiveVideoAssetPosterFetchedFailed(
				formattedError
			)
		)
	}
}

function *getAssetVideo(videoUid, jwt) {
	try {
		const video = yield callAny(
			interactiveVideoSync.getAssetVideo,
			videoUid,
			jwt
		)
		yield put(
			interactiveVideoActions.interactiveVideoAssetVideoFetched(video)
		)
	} catch (error) {
		const formattedError = formatError(error)
		yield put(
			interactiveVideoActions.interactiveVideoAssetVideoFetchedFailed(
				formattedError
			)
		)
	}
}

function *submitOeqAnswer(action) {
	try {
		const interactiveVideoOeqSubmissionData =
			yield select(
				interactiveVideoSelectors.interactiveVideoOeqSubmissionSelector(
					action.questionUid,
				)
			)
		const draft = interactiveVideoOeqSubmissionData.draft
		if (draft && draft.state === draftStateTypes.SUBMITTING) {
			yield callAny(
				interactiveVideoSync.createOeqSubmission,
				interactiveVideoOeqSubmissionData.jwt,
				interactiveVideoOeqSubmissionData.mediaSessionUid,
				action.questionUid,
				interactiveVideoOeqSubmissionData.participantUid,
				draft.value,
			)
			yield put(
				interactiveVideoActions.submitOeqAnswerSuccess(action.questionUid)
			)
		}
	} catch (error) {
		yield put(interactiveVideoActions.submitOeqAnswerFailed(
			formatError(error),
			action.questionUid,
		))
	}
}

function *submitMcqAnswer(action) {
	try {
		const interactiveVideoMcqSubmissionData =
			yield select(
				interactiveVideoSelectors.interactiveVideoMcqSubmissionSelector(
					action.questionUid,
				)
			)
		const draft = interactiveVideoMcqSubmissionData.draft
		if (draft && draft.state === draftStateTypes.SUBMITTING) {
			yield callAny(
				interactiveVideoSync.createMcqSubmission,
				interactiveVideoMcqSubmissionData.jwt,
				interactiveVideoMcqSubmissionData.mediaSessionUid,
				action.questionUid,
				interactiveVideoMcqSubmissionData.participantUid,
				draft.value,
			)
			yield put(
				interactiveVideoActions.submitMcqAnswerSuccess(action.questionUid)
			)
		}
	} catch (error) {
		yield put(interactiveVideoActions.submitMcqAnswerFailed(
			formatError(error),
			action.questionUid,
		))
	}
}

function *mcqShareSolution() {
	try {
		const interactiveVideoMcqShareSolution =
			yield select(
				interactiveVideoSelectors.interactiveVideoMcqShareSolution
			)
		yield callAny(
			interactiveVideoSync.shareMcqSolution,
			interactiveVideoMcqShareSolution.jwt,
			interactiveVideoMcqShareSolution.mediaSessionUid,
			interactiveVideoMcqShareSolution.questionUid,
		)
	} catch (error) {
		yield put(interactiveVideoActions.shareMcqSolutionFailed(
			formatError(error),
		))
	}
}

function *mcqUnshareSolution() {
	try {
		const interactiveVideoMcqShareSolution =
			yield select(
				interactiveVideoSelectors.interactiveVideoMcqShareSolution
			)
		yield callAny(
			interactiveVideoSync.unshareMcqSolution,
			interactiveVideoMcqShareSolution.jwt,
			interactiveVideoMcqShareSolution.mediaSessionUid,
			interactiveVideoMcqShareSolution.questionUid,
		)
	} catch (error) {
		yield put(interactiveVideoActions.unshareMcqSolutionFailed(
			formatError(error),
		))
	}
}

function *oeqShareParticipantSubmission(action) {
	try {
		const interactiveVideoMcqShareSolution =
			yield select(
				interactiveVideoSelectors.interactiveVideoOeqShareParticipantSubmission
			)
		yield callAny(
			interactiveVideoSync.shareOeqParticipantSubmission,
			interactiveVideoMcqShareSolution.jwt,
			interactiveVideoMcqShareSolution.mediaSessionUid,
			interactiveVideoMcqShareSolution.questionUid,
			action.participantUid,
		)
	} catch (error) {
		yield put(interactiveVideoActions.shareParticipantSubmissionFailed(
			action.participantUid, formatError(error),
		))
	}
}

function *oeqUnshareParticipantSubmission(action) {
	try {
		const interactiveVideoMcqShareSolution =
			yield select(
				interactiveVideoSelectors.interactiveVideoOeqShareParticipantSubmission
			)
		yield callAny(
			interactiveVideoSync.unshareOeqParticipantSubmission,
			interactiveVideoMcqShareSolution.jwt,
			interactiveVideoMcqShareSolution.mediaSessionUid,
			interactiveVideoMcqShareSolution.questionUid,
			action.participantUid,
		)
	} catch (error) {
		yield put(interactiveVideoActions.unshareParticipantSubmissionFailed(
			action.participantUid, formatError(error),
		))
	}
}

function *getInteractiveVideoLesson() {
	try {
		const interactiveVideoRequestData =
				yield select(interactiveVideoSessionInitializationSelector)
		const response =
			yield callAny(
				interactiveVideoSync.getInteractiveVideoLesson,
				interactiveVideoRequestData.externalUid,
				interactiveVideoRequestData.jwt
			)
		yield put(
			interactiveVideoActions.interactiveVideoLessonFetched(
				response.payload
			)
		)
	} catch (error) {
		const formattedError = formatError(error)
		yield put(
			interactiveVideoActions.interactiveVideoLessonFetchFailed(
				formattedError
			)
		)
	}
}

function *getNotifications() {
	try {
		const interactiveVideoRequestData =
				yield select(interactiveVideoSessionInitializationSelector)
		const response = yield callAny(interactiveVideoSync.getNotifications, interactiveVideoRequestData.jwt)
		yield put(interactiveVideoActions.interactiveVideoNotificationsFetchSuccess(response.payload))
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.interactiveVideoNotificationsFetchFailed(formattedError))
	}
}

function *interactiveModeToggleConfirmDismissed(action) {
	try {
		if (action.doNotShowAgain) {
			yield callAny(userSync.loginNotification, confirmTypes.FEATURE_INTERACTIVE_VIDEO_IM_TOGGLE_CONFIRM_ID,
				buttonUserTypes.BUTTON_GOT_IT_CONTINUE, actionUserTypes.ACTION_CONTINUE, 0)
		}
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.interactiveModeToggleConfirmDismissedFailed(formattedError))
	}
}

function *initializeStandalone(action) {
	try {
		const interactiveStandaloneData = yield select(interactiveVideoSessionInitializationSelector)
		const previewTypes = [
			sessionTypes.EMBED,
			sessionTypes.PREVIEW,
		]

		if (previewTypes.includes(interactiveStandaloneData.sessionType)) {
			yield call(initializeStandalonePreview)
		} else {
			yield call(initializeStandaloneSession,
				action.uid,
				action.jwt,
				action.role
			)
		}
	} catch (err) {
		//
	}
}

function *interactiveModePostSessionInformationDismissed(action) {
	try {
		if (action.doNotShowAgain) {
			yield callAny(userSync.loginNotification,
				confirmTypes.FEATURE_INTERACTIVE_VIDEO_POST_SESSION_INFORMATION_CONFIRM_ID,
				buttonUserTypes.BUTTON_GOT_IT_CONTINUE, actionUserTypes.ACTION_CONTINUE, 0)
		}
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.interactiveModeToggleConfirmDismissedFailed(formattedError))
	}
}

function *submitCurrentActivity(action) {
	if (!action.canDispatchOnSlideChange) {
		return
	}
	const activity = yield select(activitySelector)
	const currentActivity = activity.currentActivity
	const submitFunctionsByActivityType = {
		[activityTypes.OPEN_ENDED_QUESTION]: submitOeqAnswer,
		[activityTypes.MULTIPLE_CHOICE_QUESTION]: submitMcqAnswer,
	}
	if (!(currentActivity.type in submitFunctionsByActivityType)) {
		return
	}
	const questionUid = currentActivity.payload.uid
	const params = {
		...action,
		questionUid,
	}
	const submitFunction = submitFunctionsByActivityType[currentActivity.type]
	yield call(submitFunction, params)
}

function *getVideoCaptions(action) {
	try {
		const videoAsset = action.video.versions[0]
		if (videoAsset.source === videoSourceTypes.YOUTUBE) {
			//TODO: now we get the youtube video id from the video path
			//once we start implementing teacher uploaded captions we'll move this logic
			//to the backend and use the asset id from the db
			const videoPath = videoAsset.videos.medium.path
			const queryString = videoPath.substr(videoPath.indexOf('?') + 1)
			const params = parse(queryString)
			const videoCaptionsResponse = yield callAny(
				interactiveVideoSync.getCaptionList,
				params.v,
			)
			const formattedResponse = yield call(youtubeCaptionsFormatter, videoCaptionsResponse, params.v)
			yield put(interactiveVideoActions.videoCaptionsFetchSuccess(formattedResponse))
		}
	} catch (error) {
		const formattedError = formatError(error)
		yield put(interactiveVideoActions.videoCaptionsFetchFailed(formattedError))
	}
}

export default [
	takeLatest(actionTypes.INTERACTIVE_VIDEO_PLAYER_INITIALIZED,
		getAsset),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER,
		actionTypes.INTERACTIVE_VIDEO_OEQ_COMPONENT_UNMOUNTED,
	], submitOeqAnswer),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER,
		actionTypes.INTERACTIVE_VIDEO_MCQ_COMPONENT_UNMOUNTED,
	], submitMcqAnswer),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_MCQ_SHARE_SOLUTION,
		mcqShareSolution),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_MCQ_UNSHARE_SOLUTION,
		mcqUnshareSolution),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_OEQ_SHARE_PARTICIPANT_SUBMISSION,
		oeqShareParticipantSubmission),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_OEQ_UNSHARE_PARTICIPANT_SUBMISSION,
		oeqUnshareParticipantSubmission),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SESSION_INITIALIZE,
		getInteractiveVideoLesson),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SESSION_INITIALIZE,
		getNotifications),
	takeLatest([
		actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_REJECTED,
		actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_ACCEPTED,
	], interactiveModeToggleConfirmDismissed),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_CONTINUE_POST_SESSION_REPORT,
		interactiveModePostSessionInformationDismissed),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_PREVIEW_INITIALIZE, getPreviewData),
	fork(initializeStatus),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_STANDALONE_INITIALIZE, initializeStandalone),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_SLIDE_CHANGED, submitCurrentActivity),
	takeLatest(actionTypes.INTERACTIVE_VIDEO_ASSET_VIDEO_FETCHED,
		getVideoCaptions),
]

export {
	getAsset,
	getAssetImage,
	getAssetVideo,
	submitOeqAnswer,
	submitMcqAnswer,
	mcqShareSolution,
	mcqUnshareSolution,
	oeqShareParticipantSubmission,
	oeqUnshareParticipantSubmission,
	getNotifications,
	interactiveModeToggleConfirmDismissed,
	initializeStatus,
	connectToSession,
	handleInitializeParticipantSubmissions,
	initializeStandalonePreview,
}
